export type DancerHit = {
  dancerId: string
  name: string
  ageYears: number
  household: string | null
  householdId: string | null
}

export type DancerSession = {
  id: string
  kind: 'class' | 'booking'
  title: string
  startTime: string
  endTime: string
  roomName: string | null
  roomColor: string | null
  instructors: string[]
  clashes: boolean
}

export type DancerDay = {
  dancerId: string
  name: string
  householdId: string | null
  household: string | null
  siblings: DancerHit[]
  date: string
  sessions: DancerSession[]
}

export async function searchDancers(q: string): Promise<DancerHit[]> {
  if (q.trim().length < 2) return []
  const res = await fetch(`/api/dancers/search?q=${encodeURIComponent(q.trim())}`)
  if (res.status === 403) throw new Error('You do not have permission to look up dancers.')
  if (!res.ok) throw new Error(`Search failed (${res.status})`)
  return res.json()
}

export async function fetchDancerDay(id: string, date: string): Promise<DancerDay> {
  const res = await fetch(`/api/dancers/${id}/day?date=${date}`)
  if (!res.ok) throw new Error(`Dancer schedule failed (${res.status})`)
  return res.json()
}

/** "17:15:00" -> minutes since midnight. */
/**
 * One parser for a `HH:mm:ss` wall time (A-17).
 *
 * ⚠ **Exported because a second copy had already appeared and already
 * diverged.** `roomDay` sorted with `slice(0, 2)` / `slice(3, 5)` where this
 * splits on the colon, so an unpadded `9:30:00` came out `NaN` there and
 * `570` here. `TimeOnly` serialises zero-padded, so the two agreed on every
 * real row — which is what makes it the kind of drift nothing fails on.
 */
export function minutes(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

export type SessionStanding = 'now' | 'next' | 'earlier' | 'later'

/**
 * Where a dancer is relative to a moment.
 *
 * Exists for the counter case: a parent who does not normally do drop-off asks
 * where their daughter should be. The answer wanted is "she is in Ballet 4 in
 * the Purple Room until six" — so the view has to lead with what is happening
 * *now*, not with a timetable to be read.
 */
export function standing(
  session: { startTime: string | null; endTime: string | null },
  nowMinutes: number,
  isToday: boolean,
): SessionStanding {
  // On any other date "now" is meaningless; everything is just the timetable.
  if (!isToday) return 'later'

  /*
   * A class with no start time meets at no time (D-84).
   *
   * The Jackrabbit import brings in 86 solos awaiting a slot; their weekday is a
   * placeholder and their start is empty. Treating a missing time as midnight
   * would make every one of them "earlier" all day, and — worse — the first one
   * would become the studio's "next class" at 00:00 every morning. No time, no
   * marker.
   */
  if (!session.startTime || !session.endTime) return 'later'

  const start = minutes(session.startTime)
  const end = minutes(session.endTime)

  // Half-open, [start, end) — D-81. Back-to-back classes are how the studio
  // runs, and a closed range marks both the finishing and the starting class as
  // "on now" at exactly the handover.
  if (nowMinutes >= start && nowMinutes < end) return 'now'
  if (nowMinutes < start) return 'next'
  return 'earlier'
}

/**
 * The one session to lead with: what is running now, else what is up next.
 *
 * Returns null once the day is over, which is a real answer — "nothing else
 * today" is what the person at the counter needs to hear.
 */
/**
 * **Generic over the session, because two screens lead with this answer**
 * (A-17, D-914).
 *
 * It was typed to `DancerSession`, whose times are non-null — but the rule it
 * holds is `standing()`'s, and that already takes the structural shape and
 * treats a missing time as *later* (D-84: a class with no start meets on no
 * date). The wall tablet's sessions have nullable times, so the choice was
 * widening this or writing the rule a third time; `sessionMarkers` already
 * reuses `standing` for exactly that reason.
 *
 * ⚠ **For one commit it was widened and then not called**, and `roomDay` held
 * a transcribed copy of the body below. So this docblock described a sharing
 * that did not exist, which is worse than no docblock — the next reader
 * inherits a claim rather than a question. Found by an audit, and the callers
 * are `WhereIsView` and `roomDay`.
 */
export function currentSession<T extends { startTime: string | null; endTime: string | null }>(
  sessions: readonly T[],
  nowMinutes: number,
  isToday: boolean,
): T | null {
  if (!isToday || sessions.length === 0) return null

  // A class with no start time sorts last rather than to NaN, where it would
  // make the whole comparison meaningless.
  const at = (s: T) => (s.startTime ? minutes(s.startTime) : Number.POSITIVE_INFINITY)

  const ordered = [...sessions].sort((a, b) => at(a) - at(b))

  return (
    ordered.find((s) => standing(s, nowMinutes, true) === 'now') ??
    ordered.find((s) => standing(s, nowMinutes, true) === 'next') ??
    null
  )
}

export function minutesNow(at: Date = new Date()): number {
  return at.getHours() * 60 + at.getMinutes()
}
